import { type Extensions } from "@tiptap/core";
import { Skeleton } from "antd";
import { memo, Suspense } from "react";

import { lazyLoadWithRetry } from "utilities/react.utilities";

import { type BTEditorStandardInterfaceComponentType } from "commonComponents/btWrappers/editor/BTEditorStandardInterface/BTEditorStandardInterface";
import type { IBTEditorStandardInterfaceProps } from "commonComponents/btWrappers/editor/BTEditorStandardInterface/BTEditorStandardInterface.types";
import { useFrequentEditorRemountSafeguard } from "commonComponents/btWrappers/editor/core/editor.utils";
import { type IStandardFeatureSetConfig } from "commonComponents/btWrappers/editor/core/extensions/ExtensionProvider.types";
import { type IBTEditorBaseProps } from "commonComponents/btWrappers/editor/editor.types";
import { ErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";

const BTEditorStandardInterface = lazyLoadWithRetry(
    () =>
        import(
            "commonComponents/btWrappers/editor/BTEditorStandardInterface/BTEditorStandardInterface"
        ),
) as BTEditorStandardInterfaceComponentType;
const ExtensionProvider = lazyLoadWithRetry(
    () => import("commonComponents/btWrappers/editor/core/extensions/ExtensionProvider"),
);

export interface IBTEditorProps<FormValues>
    extends IBTEditorBaseProps<FormValues>,
        Pick<IBTEditorStandardInterfaceProps<FormValues>, "formMode"> {
    /**
     * **Should be memoized.**
     *
     *
     * Optional configurations and any additional event handler bindings to the many editor
     * controls/extensions.
     *
     * @example
     * // Overrides document schema to force bold text
     * const myConfig = React.useMemo(() => ({
     *      document: {
     *          extend: {
     *              content: "paragraph",
     *          },
     *      },
     *      paragraph: {
     *          extend: {
     *              renderHTML({ HTMLAttributes }) {
     *                  return ["p", HTMLAttributes, ["strong", {}, 0]];
     *              },
     *          },
     *      },
     *    },
     * }), []);
     * <BTEditor
     *     extensionConfig={myConfig}
     * />
     */
    extensionConfig?: IStandardFeatureSetConfig;
}

function BTEditorInternal<FormValues = undefined>({
    extensionConfig,
    value,
    ...otherProps
}: IBTEditorProps<FormValues>) {
    useFrequentEditorRemountSafeguard(value);
    return (
        <ErrorBoundary errorMessage="There was an error loading the rich text editor. Please refresh the page or contact support if this issue persists.">
            {/* eslint-disable-next-line react/forbid-elements*/}
            <Suspense fallback={<Skeleton.Input size="large" />}>
                <ExtensionProvider featureSet="standard" extensionConfig={extensionConfig}>
                    {(extensions: Extensions) => (
                        // eslint-disable-next-line react/forbid-elements
                        <BTEditorStandardInterface
                            extensions={extensions}
                            value={value}
                            {...otherProps}
                        />
                    )}
                </ExtensionProvider>
            </Suspense>
        </ErrorBoundary>
    );
}

export const BTEditor = memo(BTEditorInternal) as typeof BTEditorInternal;
